import { configureStore, getDefaultMiddleware } from '@reduxjs/toolkit';
import { isType } from 'ts-action';
import * as signalActions from '../features/ecg/actions/signal.actions';
import { undoMiddleware } from '../features/undo/middlewares/undo.middleware';
import * as rawSignalActions from '../features/raw-signal/actions/raw-signal.actions';
import * as clusterActions from '../features/clustering/actions/cluster.actions';
import rootReducer from '../store/rootReducer';

const middleware = getDefaultMiddleware({
  immutableCheck: {
    ignoredPaths: [
      'delineation', // stores a lot of waves
      'scatter', // stores a large array of qrs onsets
      'signal', // stores the signal
      'undo', // gets large in long sessions
      'wave', // stores a lot of waves
      'rawSignal', // stores the raw signal
    ],
  },
  serializableCheck: false,
}).concat([undoMiddleware]);

const store = configureStore({
  reducer: rootReducer,
  middleware,
  devTools: {
    actionsDenylist: ['blacklisted'], // don't log an action if `blacklisted` is in the type
    actionSanitizer: (action) => {
      if (isType(action, signalActions.loaded) && action.signal.leads != null) {
        // the lead data is heavy to sanitize and useless in the devtools
        return {
          ...action,
          signal: { ...action.signal, leads: 'leadData' },
        };
      }

      if (
        isType(action, rawSignalActions.load) &&
        action.rawSignal.leads != null
      ) {
        return {
          ...action,
          rawSignal: { ...action.rawSignal, leads: 'leadData' },
        };
      }

      if (isType(action, clusterActions.load) && action.clusters.length > 0) {
        return {
          ...action,
          clusters: [],
        };
      }

      return action;
    },
    stateSanitizer: (state: any): any => {
      let sanitizedState = { ...state };
      if (sanitizedState?.signal?.leads) {
        // the lead data is heavy to sanitize and useless in the devtools
        sanitizedState = {
          ...sanitizedState,
          signal: { ...sanitizedState.signal, leads: {} },
        };
      }

      if (sanitizedState?.rawSignal?.leads) {
        sanitizedState = {
          ...sanitizedState,
          rawSignal: { ...sanitizedState.rawSignal, leads: {} },
        };
      }

      if (sanitizedState?.cluster.ids.length > 0) {
        sanitizedState = {
          ...sanitizedState,
          cluster: { ...sanitizedState.cluster, ids: [], entities: {} },
        };
      }

      if (sanitizedState?.undo?.undoStack.length > 0) {
        sanitizedState = {
          ...sanitizedState,
          undo: { ...sanitizedState.undo, undoStack: [] },
        };
      }
      return sanitizedState;
    },
  },
});

if (process.env.NODE_ENV === 'development' && module.hot) {
  module.hot.accept('../store/rootReducer', () => {
    const newRootReducer = require('../store/rootReducer').default;
    store.replaceReducer(newRootReducer);
  });
}

export default store;
